import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getPricingSettings,
  updatePricingSettings,
  type PricingSettings as Settings,
  type PricingSettingKey,
} from '@/api/pricing'
import { ApiError } from '@/api/client'
import { EditableField, type EditableValue } from '@/components/EditableField'
import { Panel } from '@/components/Panel'

const SHIPPING_FIELDS: { key: PricingSettingKey; label: string; hint: string }[] = [
  { key: 'brief', label: 'Brief', hint: 'Versandkosten Brief (€)' },
  { key: 'warenpost', label: 'Warenpost', hint: 'Versandkosten Warenpost (€)' },
  { key: 'dhl', label: 'DHL', hint: 'Versandkosten DHL-Paket (€)' },
  { key: 'gls', label: 'GLS', hint: 'netto, ohne Verpackung' },
  { key: 'gls_uebergroesse_150', label: 'GLS Übergröße bis 150 cm', hint: 'netto, ohne Verpackung — nicht sorterfähig' },
  { key: 'gls_uebergroesse_gt150', label: 'GLS Übergröße über 150 cm', hint: 'netto, ohne Verpackung — Überlänge' },
  { key: 'verpackung', label: 'Verpackung', hint: 'Verpackungs-Zuschlag (€)' },
  { key: 'shopify_versand_de', label: 'Shopify Kundenversand DE (€)', hint: 'wird bei Plus-Versand vom Leitpreis abgezogen' },
  { key: 'bump_threshold', label: 'Hochstufungs-Schwelle €', hint: 'Ab diesem Preis Brief -> Warenpost' },
]

// Dieselbe Farbsprache wie STACK_PARTS in der Set-Vorschau.
const STACK_FIELDS = [
  { key: 'mwst', label: 'MwSt %', part: 'USt', color: 'bg-ink-2', hint: 'wirkt auf alle Preise; Anteil am Bruttopreis = MwSt/(100+MwSt)' },
  { key: 'amazon_gebuehr_pct', label: 'Amazon-Gebühr %', part: 'Amazon-Gebühr', color: 'bg-primary' },
  { key: 'werbung_pct', label: 'Werbung %', part: 'Werbung', color: 'bg-coral' },
  { key: 'puffer_pct', label: 'Puffer %', part: 'Puffer', color: 'bg-warn' },
] satisfies { key: PricingSettingKey; label: string; part: string; color: string; hint?: string }[]

const fmtPct = (value: number) => `${value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`

export default function PricingSettings() {
  const queryClient = useQueryClient()
  const { data: settings, isLoading } = useQuery({
    queryKey: ['pricing-settings'],
    queryFn: getPricingSettings,
    staleTime: 60_000,
  })

  const mutation = useMutation({
    mutationFn: (item: { key: PricingSettingKey; value: number }) =>
      updatePricingSettings([item]),
    onSuccess: (fresh: Settings) => {
      queryClient.setQueryData(['pricing-settings'], fresh)
      toast.success('Einstellung gespeichert')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen'),
  })

  async function saveSetting(key: PricingSettingKey, value: EditableValue) {
    const num = typeof value === 'number' ? value : Number(value)
    if (Number.isNaN(num)) throw new Error('Keine gültige Zahl')
    await mutation.mutateAsync({ key, value: num })
  }

  const parts = settings ? STACK_FIELDS.map(field => ({
    ...field,
    pct: field.key === 'mwst'
      ? Math.round(100 * settings.mwst / (100 + settings.mwst) * 10000) / 10000
      : settings[field.key],
  })) : []
  const remaining = settings ? 100 - settings.fees : 0

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-2xl">
      <header>
        <h1 className="font-display font-bold text-2xl text-ink">Versandeinstellungen</h1>
        <p className="text-sm text-ink-2 mt-1">
          Konstanten für die VK-Preis-Kalkulation. Änderungen wirken sofort auf neue Vorschauen.
        </p>
      </header>

      <Panel title="Versandkosten" subtitle="Werte anklicken zum Bearbeiten">
        {isLoading || !settings ? (
          <div className="py-8 text-center text-ink-2">Lade…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-1">
            {SHIPPING_FIELDS.map((f) => (
              <EditableField
                key={f.key}
                type="number"
                step="0.01"
                label={f.label}
                hint={f.hint}
                mono
                value={settings[f.key]}
                onSave={(v) => saveSetting(f.key, v)}
              />
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Kostenstapel" subtitle="Abzüge vom Bruttopreis — gelten für alle Preise">
        {isLoading || !settings ? (
          <div className="py-8 text-center text-ink-2">Lade…</div>
        ) : (
          <div className="space-y-6">
            <div>
              <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
                <span className="text-xs text-ink-2">100 % Bruttopreis</span>
                <div className="ml-auto text-right">
                  <div className="text-xs text-ink-2 mb-1">Summe Abzüge</div>
                  <div className="font-mono text-3xl font-semibold text-ink tabular-nums">{fmtPct(settings.fees)}</div>
                </div>
              </div>
              <div
                role="img"
                aria-label={`100 % Bruttopreis: ${parts.map(part => `${part.part} ${fmtPct(part.pct)}`).join(', ')}; Summe Abzüge ${fmtPct(settings.fees)}; bleibt für EK, Versand und Marge ${fmtPct(remaining)}`}
                className="flex h-4 w-full overflow-hidden rounded-sm bg-panel-2"
              >
                {parts.map(part => (
                  <span
                    key={part.key}
                    aria-hidden="true"
                    className={`${part.color} transition-[width] duration-300 motion-reduce:transition-none`}
                    style={{ width: `${part.pct}%` }}
                  />
                ))}
                <span
                  aria-hidden="true"
                  className="bg-panel-2 transition-[width] duration-300 motion-reduce:transition-none"
                  style={{ width: `${remaining}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-ink-2 text-right">bleibt für EK, Versand und Marge · {fmtPct(remaining)}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-1">
              {parts.map(field => (
                <div key={field.key} className="relative pl-4 [&_input]:min-w-0">
                  <span aria-hidden="true" className={`absolute left-0 top-0.5 size-2 rounded-full ${field.color}`} />
                  <EditableField
                    type="number"
                    step="0.0001"
                    label={field.label}
                    hint={field.key === 'mwst' ? `${field.hint} · ${fmtPct(field.pct)} am Brutto` : field.hint}
                    mono
                    value={settings[field.key]}
                    onSave={(v) => saveSetting(field.key, v)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </Panel>
    </div>
  )
}
